'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import type { PageSection } from '@/lib/cms';

import { getOrigin } from '@/lib/env';

const getBaseUrl = () => getOrigin();

function imageUrl(path?: string | null): string | null {
  if (!path) return null;
  const base = getBaseUrl();
  const p = path && path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export function CmsFeaturesSection({ section }: { section: PageSection }) {
  const items = section.items ?? [];
  const title = section.title ?? 'Features';
  const description = section.content;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-bg">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-landing-text">{title}</h2>
          {description && (
            <p className="mt-2 text-landing-muted max-w-2xl mx-auto">{description}</p>
          )}
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item, i) => {
            const img = imageUrl(item.image);
            return (
              <motion.div
                key={item.id ?? i}
                className="p-6 rounded-2xl border border-landing-border bg-landing-bg hover:shadow-lg hover:border-moss/30 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                {img && (
                  <div className="w-12 h-12 rounded-xl bg-moss/10 flex items-center justify-center mb-4 overflow-hidden">
                    <img src={img} alt="" className="w-10 h-10 object-cover" />
                  </div>
                )}
                {!img && item.icon && (
                  <div className="w-12 h-12 rounded-xl bg-moss/10 flex items-center justify-center mb-4 text-2xl">
                    {item.icon}
                  </div>
                )}
                <h3 className="text-lg font-semibold text-landing-text">{item.title ?? 'Feature'}</h3>
                {item.subtitle && (
                  <p className="text-sm text-moss mt-0.5">{item.subtitle}</p>
                )}
                {item.description && (
                  <p className="mt-2 text-landing-muted text-sm">{item.description}</p>
                )}
                {item.button_text && item.button_link && (
                  <Link
                    href={item.button_link}
                    className="inline-block mt-4 text-moss font-medium text-sm hover:underline"
                  >
                    {item.button_text} →
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
